import { SHighlightText } from '@setvi/shared/components';

import { valueToMonetary } from 'pages/products/utils';
import { PricingMode } from 'pages/auto-rfq/store/rfqConfig';
import { Box } from '@material-ui/core';

interface PricingDisplayProps {
  cost?: number | string;
  sell?: number | string;
  search: string;
  rfqToQuoteEnabled: boolean;
  pricingMode?: `${PricingMode}`;
  defaultMarginEnabled?: boolean;
  showEndDot?: boolean;
  // Manually added items carry a single typed-in price (saved as `sell`, with no
  // `cost`). Legacy manual entries set cost = sell = price so the number always
  // showed, regardless of the tenant's pricing mode. When `isManual` is set we
  // mirror whichever value is present into the missing one, so a manual item's
  // price is never hidden by the cost-only / no-quote safeguards below.
  isManual?: boolean;
}

const PricingDisplay = ({
  cost,
  sell,
  search,
  rfqToQuoteEnabled,
  pricingMode,
  defaultMarginEnabled,
  showEndDot,
  isManual
}: PricingDisplayProps) => {
  // See `isManual` above: a typed-in manual entry only has `sell`, so surface it
  // as the cost too (and vice versa). No-op for catalog picks, which carry both.
  const effectiveCost = isManual ? (cost ?? sell) : cost;
  const effectiveSell = isManual ? (sell ?? cost) : sell;

  // If rfqToQuoteEnabled is false, show cost only if it exists
  if (!rfqToQuoteEnabled) {
    if (!effectiveCost) return null;

    return (
      <Box
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 4
        }}>
        <SHighlightText
          value={`Cost: ${valueToMonetary(Number(effectiveCost))}`}
          searchQuery={search}
          size="sm"
          maxLines={1}
          fontColor="#333D47"
          weight="bold"
        />
        {showEndDot && <>•</>}
      </Box>
    );
  }

  // If rfqToQuoteEnabled is true, apply pricing mode logic
  if (pricingMode === PricingMode.NO_PRICING) {
    return null;
  }

  const costElement = effectiveCost ? (
    <SHighlightText
      value={`Cost: ${valueToMonetary(Number(effectiveCost) || 0)}`}
      searchQuery={search}
      size="sm"
      maxLines={1}
      fontColor="#333D47"
      weight="bold"
    />
  ) : null;

  const sellElement = effectiveSell ? (
    <SHighlightText
      value={`Sell: ${valueToMonetary(Number(effectiveSell) || 0)}`}
      searchQuery={search}
      size="sm"
      maxLines={1}
      fontColor="#333D47"
      weight="bold"
    />
  ) : null;

  if (pricingMode === PricingMode.COST_ONLY && !defaultMarginEnabled) {
    return costElement;
  }

  if (
    pricingMode === PricingMode.CUSTOMER_PRICING ||
    (pricingMode === PricingMode.COST_ONLY && defaultMarginEnabled)
  ) {
    return (
      <Box
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 4
        }}>
        {sellElement}
        {sellElement && <>•</>}
        {costElement}
        {costElement && showEndDot && <>•</>}
      </Box>
    );
  }

  return null;
};

export default PricingDisplay;
